import './ExternalApp.css';

import { FC, SyntheticEvent, useEffect, useMemo, useRef } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import useRoutes from '../../hooks/useRoutes';
import { useDataset } from '../../api/hooks/useDataset';
import { useUserState } from '../../hooks/context/useUserState';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import { useExternalAppMessages } from '../../hooks/useExternalAppMessages';
import { useTranslate } from '../../hooks/context/useTranslate';
import {
  EXTERNAL_APP_CONTROL_PARAM,
  EXTERNAL_APP_SCOPE_PARAM,
  EXTERNAL_APP_SUBSET_PARAM,
  EXTERNAL_APP_TOKEN_MESSAGE,
  EXTERNAL_APP_URL_PARAM,
} from '../../constants';
import {
  ApplicationControlAction,
  GlobalLocationStateType,
} from '../../types/types';
import { getAbsoluteUrlOrigin } from '../../utils/getAbsoluteUrlOrigin';
import { findExternalAppControls } from '../../utils/datasetControl/findExternalAppControls';

// Iframe page of an 'app' control — resolved url in the query, token via postMessage.
const ExternalApp: FC = () => {
  const history = useHistory();
  const routes = useRoutes();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { search, state } = useLocation<GlobalLocationStateType>();
  const {
    state: { token },
  } = useUserState();
  const {
    dataset,
    slaveDatasets,
    datasetId: contextDatasetId,
  } = useDatasetContext();
  const { t } = useTranslate();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const appUrl = searchParams.get(EXTERNAL_APP_URL_PARAM);
  const controlId = searchParams.get(EXTERNAL_APP_CONTROL_PARAM);
  const scope =
    searchParams.get(EXTERNAL_APP_SCOPE_PARAM) === 'subset'
      ? 'subset'
      : 'dataset';
  const subsetDatasetId = searchParams.get(EXTERNAL_APP_SUBSET_PARAM) ?? '';

  // The response-actions scope comes from the query, not from a ScopeProvider.
  const { getResponseActionHandler } = useResponseActionsContext(scope);

  // Slave definitions load only by opening a subset — after F5 the page refetches its own.
  const slaveDataset = subsetDatasetId
    ? slaveDatasets?.[subsetDatasetId]
    : undefined;
  const {
    data: fetchedSlaveDataset,
    error: slaveDatasetError,
    fetch: fetchSlaveDataset,
  } = useDataset(subsetDatasetId);
  // Data of a previous subset id must not validate this one.
  const resolvedSlaveDataset =
    slaveDataset ??
    (fetchedSlaveDataset?.datasetId === subsetDatasetId
      ? fetchedSlaveDataset
      : undefined);
  const needsSlaveFetch =
    scope === 'subset' &&
    !!subsetDatasetId &&
    !resolvedSlaveDataset &&
    !slaveDatasetError;

  // Holds the last fetched id, so a subset change without unmount refetches.
  const slaveFetchStartedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!needsSlaveFetch || slaveFetchStartedRef.current === subsetDatasetId) {
      return;
    }
    slaveFetchStartedRef.current = subsetDatasetId;
    fetchSlaveDataset();
  }, [needsSlaveFetch, subsetDatasetId, fetchSlaveDataset]);

  // The forgeable query url is trusted only when its origin matches a definition link's origin.
  const controls = findExternalAppControls(
    controlId,
    scope === 'subset' ? [resolvedSlaveDataset] : [dataset]
  );
  const appOrigin = appUrl ? getAbsoluteUrlOrigin(appUrl) : undefined;
  const allowedOrigin =
    appOrigin &&
    controls.some(
      (control) =>
        getAbsoluteUrlOrigin(
          (control.action as ApplicationControlAction).link
        ) === appOrigin
    )
      ? appOrigin
      : undefined;

  useExternalAppMessages({
    allowedOrigin,
    iframeRef,
    getResponseActionHandler,
    onClose: () =>
      history.push(state?.fromPathname ?? routes.datasetList(datasetId)),
  });

  // History navigation: the context may still hold the previous dataset — no
  // decision until withDatasetScope syncs it and the definitions reload.
  if (contextDatasetId !== datasetId) {
    return null;
  }

  // No decision while the subset definition is still being (re)fetched.
  if (needsSlaveFetch) {
    return null;
  }

  if (!appUrl || !allowedOrigin) {
    return <Redirect to={routes.datasetList(datasetId)} />;
  }

  // Re-posted on every iframe load; without a token nothing is sent.
  const handleIframeLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    if (!token) {
      return;
    }
    event.currentTarget.contentWindow?.postMessage(
      { type: EXTERNAL_APP_TOKEN_MESSAGE, token },
      allowedOrigin
    );
  };

  return (
    // Sandbox blocks top-navigation and popups. Only the host origin is exposed
    // so the child can authenticate the app:token sender without seeing the
    // dataset route or its query string.
    // Accepted limitation: a same-origin embed is first-party code — the sandbox cannot isolate it.
    <iframe
      ref={iframeRef}
      className="external-app-iframe"
      src={appUrl}
      title={dataset?.label ?? t('externalApp.title')}
      onLoad={handleIframeLoad}
      sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals"
      referrerPolicy="origin"
      allow=""
    />
  );
};

export default ExternalApp;
